<template>
  <div
    v-if="!badgeAdded"
    id="issuerBadgeDiv">
    <v-row>
      <v-col
        cols=7>
        <v-select
          id="issuerSelect"
          v-model="issuerProfile"
          v-if="schoolIssuers.length || !schoolIssuersLoaded"
          :loading="schoolIssuersLoading"
          label="Badge issuer"
          class="mr-2"
          :items="schoolIssuers"
          item-text="name"
          :disabled="isUpdating"
          @input="handleIssuerSelected"
          hide-details="true"
          :color="playlistColor"
          no-data-text="You don't have any issuers.  Create some on Badgr and try again!"
        ></v-select>
        <v-alert
          v-else
          id="noIssuersAlert"
          dense
          type="warning">
          Your Badgr profile doesn't have any issuers.  Create one in Badgr then try again.
        </v-alert>
      </v-col>
      <v-col
        v-if="issuerProfile"
        class="d-flex align-center justify-center">
        <v-btn
          v-if="!badgrIssuerPending && !badgrIssuerApproved"
          id="requestIssuerAccessButton"
          dark
          :color="playlistColor"
          @click="requestIssuerAccess"
          :loading="isUpdating">
          Request Issuer Access
        </v-btn>
        <v-alert
          v-else-if="badgrIssuerPending"
          id="pendingAlert"
          dense
          outlined
          type="info"
        >Issuer request pending.</v-alert>
      </v-col>
    </v-row>
    <v-row
      v-if="badgrIssuerApproved">
      <v-col
        cols=7
        class="pt-0">
        <v-autocomplete
          id="badgeAutocomplete"
          v-model="selectedBadge"
          label="Select Badge"
          :items="issuerBadges"
          item-text="name"
          item-value="entityId"
          :disabled="isUpdating"
          :color="playlistColor"
          :loading="loadingBadges"
          chips
          no-data-text="You don't have any badges.  Create one!"
        >
          <template v-slot:selection="data">
            <v-chip
              v-bind="data.attrs"
              :input-value="data.selected"
              close
              @click:close="unselectBadge()"
            >
              <v-avatar left>
                <v-img
                  :src="data.item.image"></v-img>
              </v-avatar>
              {{ data.item.name }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <v-list-item-avatar>
              <img :src="data.item.image">
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col>
        <v-btn
          id="addCreateBadgeButton"
          dark
          :color="playlistColor"
          @click="handleAddBadge"
          :loading="isUpdating">
          {{ selectedBadge ? 'Add badge' : 'Create new badge' }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
  <div
    v-else-if="badgrLoaded"
    id="badgeRow">
    <v-row>
      <v-col>
        <v-list>
          <v-list-item
            class="outlinedListItem">
            <v-list-item-avatar>
              <v-img
                :src="badgeAdded.image"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title
                class="text-left">
                <a :href="badgeAdded.openBadgeId" target="_blank">{{ badgeAdded.name }}</a>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                icon
                @click="removeBadge()">
                <v-icon color="grey lighten-1">mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import _ from 'lodash';

export default {
  name: 'BadgrSettings',
  data: () => ({
    badgrIdentities: [],
    credentialBadge: false,
    issuerBadges: [],
    issuerProfile: null,
    loadingBadges: false,
    microcredentialKey: 1,
    microcredentialProviders: ['None'],
    playlistColor: null,
    schoolIssuers: [],
    schoolIssuersLoaded: false,
    schoolIssuersLoading: false,
    selectedBadge: null,
  }),
  computed: {
    ...mapState([
      'badgrLoaded',
      'courseHash',
      'isLoaded',
      'isLti',
      'isUpdating',
      'microcredentialService',
      'playlist',
      'sectionId',
      'user',
    ]),
    ...mapGetters([
      'restrictPlaylistAccess',
    ]),
    badgeAdded() {
      return _.get(this.playlist, 'badge.badgr.badge');
    },
    badgrIssuerApproved() {
      return (
        this.issuerProfile
        && !_.get(this.selectedIssuer, 'pending', false)
        && _.get(this.selectedIssuer, 'approved', false)
      );
    },
    badgrIssuerPending() {
      return (
        this.issuerProfile
        && _.get(this.selectedIssuer, 'pending', false)
      );
    },
    selectedIssuer() {
      return _.find(this.schoolIssuers, { name: this.issuerProfile });
    },
  },
  methods: {
    ...mapMutations([
      'setBadgrLoaded',
      'setIsUpdating',
      'setPlaylist',
      'setSlideout',
      'toggleSnackbar',
    ]),
    ...mapActions([
      'getInitialBadgrData',
      'requestBadgrIssuerAccess',
    ]),
    async handleAddBadge() {
      try {
        // If a badge is selected - add it to the Playlist
        if (this.selectedBadge) {
          this.setIsUpdating(true);
          const { data: updatedPlaylist } = await this.$api.playlist.addBadgrBadge({
            playlistId: this.playlist.id,
            badge: _.find(this.issuerBadges, { entityId: this.selectedBadge }),
            badgeService: this.microcredentialService,
            issuerEntityId: this.selectedIssuer.entityId,
          });
          this.setPlaylist(updatedPlaylist);
          this.setBadgrLoaded(true);
        } else {
          this.setSlideout({
            type: 'badgeCreator',
            issuerEntityId: this.selectedIssuer.entityId,
          });
        }
      } catch(e) {
        this.toggleSnackbar({
          text: 'Error adding badge.',
          color: 'error',
          timeout: 5000,
        });
      } finally {
        this.microcredentialKey += 1;
        this.setIsUpdating(false);
      }
    },
    async handleUpdateBadgeEnabled() {
      try {
        // If there is a badge added to playlist and instructor selects 'None' for badge service - deactivate badge
        if (
          this.badgrLoaded
        ) {
          this.setIsUpdating(true);
          await this.$api.playlist.disableBadge({
            enableBadge: this.microcredentialService !== 'None',
            playlistId: this.playlist.id,
          });
        }
      } catch(e) {
        this.toggleSnackbar({
          text: 'Error deactivating badge.',
          color: 'error',
          timeout: 2500,
        });
      } finally {
        this.setIsUpdating(false);
      }
    },
    async handleIssuerSelected() {
      this.unselectBadge();
      if (this.selectedIssuer && this.selectedIssuer.approved) {
        this.loadingBadges = true;
        const { data: badges } = await this.$api.badgr.getBadges(this.selectedIssuer.entityId);
        this.loadingBadges = false;
        this.issuerBadges = badges;
      }
    },
    handleOpenBadgeLink() {
      window.open(`${this.badgeAdded.openBadgeId}`, '_blank');
    },
    async removeBadge() {
      this.setIsUpdating(true);
      const { data: updatedPlaylist } = await this.$api.playlist.removeBadge({
        badgeEntityId: this.badgeAdded.entityId,
        badgeService: this.microcredentialService,
        playlistId: this.playlist.id,
      });
      this.setPlaylist(updatedPlaylist);
      this.selectedBadge = null;
      this.issuerProfile = null;
      this.setIsUpdating(false);
    },
    async requestIssuerAccess() {
      if (
        !_.has(this, 'selectedIssuer.pending')
        && !_.has(this, 'selectedIssuer.approved')
      ) {
        await this.requestBadgrIssuerAccess(this.selectedIssuer);
        _.find(this.schoolIssuers, { name: this.issuerProfile }).pending = true;
      }
    },
    unselectBadge() {
      this.selectedBadge = null;
    },
  },
  async mounted() {
    /*
      The default_color is set to a standard hex color (#2196F3) by default as it is the only way to remove
      the transparency picker from the Vuetify color picker.  Then on mounted we update it to the stored
      value from the DB
    */
    this.playlistColor = _.get(this, 'playlist.default_color') ? this.playlist.default_color : '#2196F3';
    this.badgeName = `${this.playlist.name} Completion Badge`;
    this.badgeDescription = `This badge certifies that the recepient has completed the playlist titled ${this.playlist.name}.`;
    try {
      this.schoolIssuersLoading = true;
      const initialBadgrData = await this.getInitialBadgrData();
      this.schoolIssuers = _.get(initialBadgrData, 'issuers', []);
      this.badgrIdentities = _.get(initialBadgrData, 'badgrTeacherIdentities', []);
    } catch(e) {
      // eslint-disable-line
      // Badgr has issues with expired tokens and not allowing us to refresh them.  Silencing error while waiting for Badgr's response.
    } finally {
      this.schoolIssuersLoading = false;
      this.schoolIssuersLoaded = true;
    }
  },
};
</script>

<style scoped>
  .outlinedListItem {
    border: 1px solid #ccc;
    border-radius: 16px;
  }
</style>
